import { AuthStorage } from "utils/auth-storage";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Cascader,
  Checkbox,
  DatePicker,
  Modal,
  Select,
  Switch,
  Tooltip,
} from "antd";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { SideBarEndService } from "apis/Services/SideBarService";
import InfoIcon from "assets/images/InfoIcon.svg";
import increaseArrow from "assets/images/increaseArrow.svg";
import { firstFilled } from "./procurementHelpers";
import styles from "./ProcurementSideMenuContent.module.css";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Resolves the stable backend identifier for the currently opened submittal row.
function resolveProcurementEntryId(content) {
  return firstFilled(content?.entryID, content?.tempId, content?.id, "");
}

// Interprets backend working-day flags for disabling picker dates.
function isWorkingDayEnabled(value) {
  return value === "1" || value === 1 || value === true;
}

// Parses holiday range boundaries from the backend schedule settings.
function parseHolidayRangeBoundary(value) {
  if (!value) {
    return null;
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [month, day, year] = value.split("-");
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate.startOf("day") : null;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [month, day, year] = value.split("/");
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate.startOf("day") : null;
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.startOf("day") : null;
}

// Parses the procurement MM-DD-YYYY format into a Day.js object for controlled pickers.
function parsePickerDate(value) {
  if (!value) {
    return null;
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [month, day, year] = value.split("-");
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate : null;
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate : null;
}

// Builds disabled weekday and holiday lookups for the drawer date picker.
function buildDisabledCalendarConfig(workingDays, nonWorkingDays) {
  const disabledWeekdays = new Set(
    DAY_NAMES.filter(
      (dayName) =>
        Object.prototype.hasOwnProperty.call(workingDays || {}, dayName) &&
        !isWorkingDayEnabled(workingDays?.[dayName]),
    ),
  );

  const disabledDates = new Set();

  (nonWorkingDays || []).forEach((holiday) => {
    const startDate = parseHolidayRangeBoundary(holiday?.startDate);
    const finishDate = parseHolidayRangeBoundary(holiday?.finishDate);

    if (!startDate || !finishDate) {
      return;
    }

    const rangeStart = startDate.isAfter(finishDate) ? finishDate : startDate;
    const rangeEnd = startDate.isAfter(finishDate) ? startDate : finishDate;
    let cursor = rangeStart;

    while (cursor.valueOf() <= rangeEnd.valueOf()) {
      disabledDates.add(cursor.format("YYYY-MM-DD"));
      cursor = cursor.add(1, "day");
    }
  });

  return { disabledWeekdays, disabledDates };
}

function LoadingLine({ width = "100%", height = 14, className = "" }) {
  return (
    <div
      className={[styles.loadingLine, className].filter(Boolean).join(" ")}
      style={{ width, height }}
    />
  );
}

function ProcurementSideMenuContentLoadingState() {
  return (
    <div className={styles.panel} aria-busy="true" aria-live="polite">
      <section className={styles.section}>
        <div className={styles.loadingSectionHeader}>
          <LoadingLine width={108} height={12} />
          <LoadingLine width={168} height={36} />
        </div>

        <div className={styles.loadingField}>
          <LoadingLine width="46%" height={12} />
          <LoadingLine height={42} />
        </div>

        <div className={styles.loadingList}>
          {Array.from({ length: 2 }).map((_, index) => (
            <div className={styles.loadingListRow} key={`manager-${index}`}>
              <LoadingLine className={styles.loadingAvatar} />
              <div className={styles.loadingListContent}>
                <LoadingLine width="62%" height={13} />
                <LoadingLine width="40%" height={11} />
              </div>
              <LoadingLine className={styles.loadingListAction} />
            </div>
          ))}
        </div>

        <div className={styles.loadingField}>
          <LoadingLine width="52%" height={12} />
          <LoadingLine height={42} />
        </div>

        <div className={styles.loadingSwitchRow}>
          <LoadingLine width={44} height={24} />
          <LoadingLine width={150} height={12} />
        </div>

        <div className={styles.actionRow}>
          <LoadingLine width={92} height={36} />
        </div>
      </section>

      <section className={styles.section}>
        <LoadingLine width={96} height={12} />

        <div className={styles.twoColumnGrid}>
          <div className={styles.loadingField}>
            <LoadingLine width="48%" height={12} />
            <LoadingLine height={42} />
          </div>

          <div className={styles.loadingField}>
            <LoadingLine width="54%" height={12} />
            <LoadingLine height={42} />
          </div>
        </div>

        <div className={styles.actionRow}>
          <LoadingLine width={132} height={36} />
        </div>
      </section>

      <section className={styles.section}>
        <LoadingLine width={72} height={12} />

        <div className={styles.loadingField}>
          <LoadingLine width="44%" height={12} />
          <LoadingLine height={42} />
        </div>

        <div className={styles.actionRow}>
          <LoadingLine width={160} height={36} />
        </div>
      </section>

      <section className={styles.section}>
        <LoadingLine width={156} height={12} />

        <div className={styles.loadingActivityList}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div className={styles.loadingActivityItem} key={`activity-${index}`}>
              <LoadingLine className={styles.loadingActivityIcon} />
              <div className={styles.loadingActivityContent}>
                <LoadingLine width="80%" height={13} />
                <LoadingLine width="52%" height={11} />
              </div>
              <LoadingLine className={styles.loadingActivityArrow} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// Renders the beta procurement drawer content with assignment, dependency, and workflow tools.
function ProcurementSideMenuContent({
  content,
  projectDetails,
  accessLevel,
  workingDays,
  nonWorkingDays,
  onResponsibleContractorChange,
  dependenciesGraph,
  setDependencies,
  handleColumnChange,
  actual,
  isLocked,
  workflowLockDetails,
  beginSyncActivity,
  finishSyncActivity,
  failSyncActivity,
}) {
  const { company_id, id } = useParams();
  const selectedEntryId = useMemo(
    () => resolveProcurementEntryId(content),
    [content],
  );
  const calendarConfig = useMemo(
    () => buildDisabledCalendarConfig(workingDays, nonWorkingDays),
    [workingDays, nonWorkingDays],
  );
  const [dependenciesData, setDependenciesData] = useState({});
  const [selectedEntryIDs, setSelectedEntryIDs] = useState([]);
  const [selectedPredecessorIDs, setSelectedPredecessorIDs] = useState([]);
  const [saveDependenciesFlag, setSaveDependenciesFlag] = useState(false);
  const [cascaderValue, setCascaderValue] = useState([]);
  const [predecessorCascaderValue, setPredecessorCascaderValue] = useState([]);
  const [contact, setContact] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedManager, setSelectedManager] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState([]);
  const [selectedAssignmentEmails, setSelectedAssignmentEmails] = useState([]);
  const [sendNotification, setSendNotification] = useState("sendNotification");
  const [overwriteField] = useState(true);
  const [isCompanyModalVisible, setIsCompanyModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isOverwriteModalVisible, setIsOverwriteModalVisible] = useState(false);
  const [selectedActualDeliveryDate, setSelectedActualDeliveryDate] = useState(
    actual || "",
  );
  const lastFetchedDependenciesEntryIdRef = useRef("");
  const selectedEntryIdRef = useRef(selectedEntryId);
  const dependenciesGraphRef = useRef(dependenciesGraph);
  const [isSidebarLoading, setIsSidebarLoading] = useState(false);

  useEffect(() => {
    selectedEntryIdRef.current = selectedEntryId;
  }, [selectedEntryId]);

  useEffect(() => {
    dependenciesGraphRef.current = dependenciesGraph;
  }, [dependenciesGraph]);

  // Resets the actual-delivery draft when the selected row changes.
  useEffect(() => {
    setSelectedActualDeliveryDate(actual || "");
  }, [actual, selectedEntryId]);

  // Loads row dependency metadata for the active submittal.
  const fetchProcurementLogRowDependencies = useCallback(async (entryId) => {
    if (!entryId) {
      setDependenciesData({});
      return;
    }

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    const requestData = {
      company_id,
      id,
      code,
      loginEmail,
    };

    try {
      const response =
        await SideBarEndService.getProcurementLogRowDependencies(requestData);
      if (selectedEntryIdRef.current !== entryId) {
        return;
      }

      const nextDependencies = response?.dependenciesDict?.[entryId] || {};
      setDependenciesData(nextDependencies);
      setDependencies?.(response?.dependenciesDict || {});
    } catch (error) {
      if (selectedEntryIdRef.current === entryId) {
        lastFetchedDependenciesEntryIdRef.current = "";
      }
      console.error("Failed to load procurement row dependencies", error);
    }
  }, [company_id, id, setDependencies]);

  // Loads the current task assignments and activity history for the active submittal.
  const fetchTasksAssignedInfo = useCallback(async (entryId) => {
    if (!entryId) {
      setContact(null);
      return;
    }

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    try {
      const response = await SideBarEndService.TasksAssignedInfo({
        code,
        loginEmail,
        company_id,
        project_id: id,
        id: entryId,
      });
      if (selectedEntryIdRef.current !== entryId) {
        return;
      }
      setContact(response);
    } catch (error) {
      console.error("Failed to load procurement assignments", error);
    }
  }, [company_id, id]);

  useEffect(() => {
    if (!selectedEntryId) {
      setContact(null);
      setDependenciesData({});
      lastFetchedDependenciesEntryIdRef.current = "";
      setIsSidebarLoading(false);
      return;
    }

    if (dependenciesGraph && Object.keys(dependenciesGraph).length > 0) {
      setDependenciesData(dependenciesGraph?.[selectedEntryId] || {});
      lastFetchedDependenciesEntryIdRef.current = selectedEntryId;
    }
  }, [dependenciesGraph, selectedEntryId]);

  useEffect(() => {
    if (!selectedEntryId) {
      return;
    }

    const entryId = selectedEntryId;
    const cachedDependenciesGraph = dependenciesGraphRef.current;
    const hasCachedDependencies =
      cachedDependenciesGraph &&
      Object.keys(cachedDependenciesGraph).length > 0;

    setIsSidebarLoading(true);
    setContact(null);

    if (hasCachedDependencies) {
      setDependenciesData(cachedDependenciesGraph?.[entryId] || {});
      lastFetchedDependenciesEntryIdRef.current = entryId;
    } else {
      setDependenciesData({});
      lastFetchedDependenciesEntryIdRef.current = "";
    }

    const pendingRequests = [fetchTasksAssignedInfo(entryId)];

    if (!hasCachedDependencies) {
      lastFetchedDependenciesEntryIdRef.current = entryId;
      pendingRequests.push(fetchProcurementLogRowDependencies(entryId));
    }

    Promise.allSettled(pendingRequests).finally(() => {
      if (selectedEntryIdRef.current === entryId) {
        setIsSidebarLoading(false);
      }
    });
  }, [
    fetchProcurementLogRowDependencies,
    fetchTasksAssignedInfo,
    selectedEntryId,
  ]);

  // Finds the cascader path for a submittal entry in the project hierarchy.
  const findPathForEntryID = useCallback(
    (entryID) => {
      if (!projectDetails?.content) {
        return null;
      }

      for (const [divisionName, subdivisions] of Object.entries(
        projectDetails.content,
      )) {
        for (const [subdivisionName, items] of Object.entries(subdivisions)) {
          const matchedItem = items.find((item) => item.entryID === entryID);

          if (matchedItem) {
            return [divisionName, subdivisionName, entryID];
          }
        }
      }

      return null;
    },
    [projectDetails],
  );

  // Builds the cascader options used by both linked-item selectors.
  const dependencyOptions = useMemo(
    () =>
      Object.entries(projectDetails?.content || {}).map(
        ([divisionName, subdivisions]) => ({
          value: divisionName,
          label: (
            <Tooltip title={divisionName} placement="right">
              {divisionName}
            </Tooltip>
          ),
          searchValue: divisionName,
          children: Object.entries(subdivisions).map(
            ([subdivisionName, items]) => ({
              value: subdivisionName,
              label: (
                <Tooltip title={subdivisionName} placement="right">
                  {subdivisionName}
                </Tooltip>
              ),
              searchValue: subdivisionName,
              children: (items || [])
                .filter(
                  (item) => resolveProcurementEntryId(item) !== selectedEntryId,
                )
                .map((item) => {
                  const labelText = item.specTitle || item.specNumber || "";
                  return {
                    value: item.entryID,
                    label: (
                      <Tooltip title={labelText} placement="right">
                        {labelText}
                      </Tooltip>
                    ),
                    searchValue: labelText,
                  };
                }),
            }),
          ),
        }),
      ),
    [projectDetails, selectedEntryId],
  );

  // Expands a cascader path into all submittal IDs beneath the chosen node.
  const getAllEntryIDsFromPath = useCallback(
    (value) => {
      const entryIDs = [];

      if (value.length === 1) {
        const divisionName = value[0];
        const division = projectDetails?.content?.[divisionName];
        if (division) {
          Object.values(division).forEach((items) => {
            (items || []).forEach((item) => {
              if (item.entryID) {
                entryIDs.push(item.entryID);
              }
            });
          });
        }
      } else if (value.length === 2) {
        const [divisionName, subdivisionName] = value;
        const items =
          projectDetails?.content?.[divisionName]?.[subdivisionName];
        (items || []).forEach((item) => {
          if (item.entryID) {
            entryIDs.push(item.entryID);
          }
        });
      } else if (value.length === 3 && value[2]) {
        entryIDs.push(value[2]);
      }

      return entryIDs;
    },
    [projectDetails],
  );

  // Updates the selected successor IDs whenever the linked-items cascader changes.
  const handleSuccessorChange = useCallback(
    (values) => {
      const allEntryIDs = [];

      values.forEach((value) => {
        allEntryIDs.push(...getAllEntryIDsFromPath(value));
      });

      setSelectedEntryIDs([...new Set(allEntryIDs)]);
      setSaveDependenciesFlag(true);
    },
    [getAllEntryIDsFromPath],
  );

  // Updates the selected predecessor IDs whenever the predecessor cascader changes.
  const handlePredecessorChange = useCallback(
    (values) => {
      const allEntryIDs = [];

      values.forEach((value) => {
        allEntryIDs.push(...getAllEntryIDsFromPath(value));
      });

      setSelectedPredecessorIDs([...new Set(allEntryIDs)]);
      setSaveDependenciesFlag(true);
    },
    [getAllEntryIDsFromPath],
  );

  // Syncs the dependency form controls with the latest backend dependency snapshot.
  useEffect(() => {
    const successorIDs = (dependenciesData?.successors || []).map(
      (successor) => successor.id,
    );
    const predecessorIDs = (dependenciesData?.predecessors || []).map(
      (predecessor) => predecessor.id,
    );
    const successorPaths = successorIDs
      .map((successorId) => findPathForEntryID(successorId))
      .filter(Boolean);
    const predecessorPaths = predecessorIDs
      .map((predecessorId) => findPathForEntryID(predecessorId))
      .filter(Boolean);

    setSelectedEntryIDs(successorIDs);
    setSelectedPredecessorIDs(predecessorIDs);
    setCascaderValue(successorPaths);
    setPredecessorCascaderValue(predecessorPaths);
    setSaveDependenciesFlag(false);
  }, [dependenciesData, findPathForEntryID]);

  // Saves any dependency additions and removals for the active submittal.
  const handleSaveDependencies = useCallback(async () => {
    if (!selectedEntryId) {
      return;
    }

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    const originalSuccessorIDs = (dependenciesData?.successors || []).map(
      (successor) => successor.id,
    );
    const originalPredecessorIDs = (dependenciesData?.predecessors || []).map(
      (predecessor) => predecessor.id,
    );
    const deletedSuccessors = originalSuccessorIDs.filter(
      (entryID) => !selectedEntryIDs.includes(entryID),
    );
    const deletedPredecessors = originalPredecessorIDs.filter(
      (entryID) => !selectedPredecessorIDs.includes(entryID),
    );
    const newSuccessors = selectedEntryIDs.filter(
      (entryID) => !originalSuccessorIDs.includes(entryID),
    );
    const newPredecessors = selectedPredecessorIDs.filter(
      (entryID) => !originalPredecessorIDs.includes(entryID),
    );

    const createdDependenciesData = {
      code,
      loginEmail,
      company_id,
      id,
      entryID: selectedEntryId,
      successorEntryIDs: newSuccessors,
      predecessorEntryIDs: newPredecessors,
    };
    const deletedDependenciesData = {
      code,
      loginEmail,
      company_id,
      id,
      entryID: selectedEntryId,
      successorEntryIDs: deletedSuccessors,
      predecessorEntryIDs: deletedPredecessors,
    };
    const syncToken = beginSyncActivity?.("Saving dependencies");

    try {
      if (newSuccessors.length || newPredecessors.length) {
        await SideBarEndService.createProcurementLogRowDependencies(
          createdDependenciesData,
        );
      }

      if (deletedSuccessors.length || deletedPredecessors.length) {
        await SideBarEndService.deleteProcurementLogRowDependencies(
          deletedDependenciesData,
        );
      }

      setSaveDependenciesFlag(false);
      finishSyncActivity?.(syncToken);
    } catch (error) {
      console.error("Error saving dependencies", error);
      failSyncActivity?.(
        syncToken,
        "Failed to save dependencies. You may no longer be synced to the latest version.",
      );
    } finally {
      void fetchProcurementLogRowDependencies(selectedEntryId);
    }
  }, [
    beginSyncActivity,
    company_id,
    dependenciesData,
    failSyncActivity,
    fetchProcurementLogRowDependencies,
    finishSyncActivity,
    id,
    selectedEntryId,
    selectedEntryIDs,
    selectedPredecessorIDs,
  ]);

  // Filters valid contacts down to those who are not already assigned to the submittal.
  const filteredOptions = useMemo(() => {
    const assignedEmails = new Set(
      (contact?.tasksAssigned?.content || []).map(
        (item) => item["Assigned To Email"],
      ),
    );
    const pickedEmails = new Set(selectedItems);

    return Object.entries(contact?.validContacts || {}).filter(
      ([email]) => !assignedEmails.has(email) && !pickedEmails.has(email),
    );
  }, [contact, selectedItems]);

  const assignedTasks = useMemo(
    () => contact?.tasksAssigned?.content || [],
    [contact],
  );

  const assignedManagers = useMemo(
    () => assignedTasks.filter((item) => item.Role === "Manager"),
    [assignedTasks],
  );

  const assignedContractors = useMemo(
    () => assignedTasks.filter((item) => item.Role === "Contractor"),
    [assignedTasks],
  );

  const selectedAssignedTasks = useMemo(
    () =>
      assignedTasks.filter((item) =>
        selectedAssignmentEmails.includes(item["Assigned To Email"]),
      ),
    [assignedTasks, selectedAssignmentEmails],
  );

  const hasSelectedAssignments = selectedAssignedTasks.length > 0;
  const isBulkRemoveDisabled =
    !hasSelectedAssignments || Number(accessLevel) < 3;

  useEffect(() => {
    const availableEmails = new Set(
      assignedTasks.map((item) => item["Assigned To Email"]),
    );

    setSelectedAssignmentEmails((previousEmails) =>
      previousEmails.filter((email) => availableEmails.has(email)),
    );
  }, [assignedTasks]);

  // Keeps the selected email cache aligned with the current manager/contractor picks.
  useEffect(() => {
    const allSelectedEmails = [
      ...selectedManager.map((manager) => manager.email),
      ...selectedContractor.map((contractor) => contractor.email),
    ];

    setSelectedItems((previousItems) =>
      previousItems.filter((email) => allSelectedEmails.includes(email)),
    );
  }, [selectedManager, selectedContractor]);

  // Converts the selected managers into the request-friendly shape used by assignTask.
  const handleManagerSelectChange = useCallback((selectedValues) => {
    const nextSelectedManagers = selectedValues.map((value) => {
      const [email, fullName] = value.split(",");
      const [firstName = "", ...lastNameParts] = fullName.split(" ");
      return {
        email,
        firstName,
        lastName: lastNameParts.join(" "),
      };
    });

    setSelectedItems((previousItems) => [
      ...new Set([
        ...previousItems,
        ...selectedValues.map((value) => value.split(",")[0]),
      ]),
    ]);
    setSelectedManager(nextSelectedManagers);
  }, []);

  // Converts the selected contractors into the request-friendly shape used by assignTask.
  const handleContractorSelectChange = useCallback((selectedValues) => {
    const nextSelectedContractors = selectedValues.map((value) => {
      const [email, fullName, companyRaw = ""] = value.split(",");
      const [firstName = "", ...lastNameParts] = fullName.split(" ");
      return {
        email,
        firstName,
        lastName: lastNameParts.join(" "),
        companyName: companyRaw.trim(),
      };
    });

    setSelectedItems((previousItems) => [
      ...new Set([
        ...previousItems,
        ...selectedValues.map((value) => value.split(",")[0]),
      ]),
    ]);
    setSelectedContractor(nextSelectedContractors);
  }, []);

  // Determines whether the save-assignments button should be enabled.
  const isAssignmentButtonVisible = selectedItems.length > 0;

  // Toggles whether the assignment workflow should send notifications.
  const handleNotificationChange = useCallback((checked) => {
    setSendNotification(checked ? "sendNotification" : "");
  }, []);

  // Computes the currently selected manager select values for the controlled Ant Select.
  const selectedManagerValues = useMemo(
    () =>
      selectedManager.map(
        (manager) =>
          `${manager.email},${manager.firstName} ${manager.lastName}`,
      ),
    [selectedManager],
  );

  // Computes the currently selected contractor select values for the controlled Ant Select.
  const selectedContractorValues = useMemo(
    () =>
      selectedContractor.map(
        (contractor) =>
          `${contractor.email},${contractor.firstName} ${contractor.lastName}, ${contractor.companyName}`,
      ),
    [selectedContractor],
  );

  // Collects all contractor company names already assigned or currently selected.
  const allContractorCompanies = useMemo(() => {
    const existingCompanies =
      (contact?.tasksAssigned?.content || [])
        .filter((item) => item.Role === "Contractor")
        .map((item) => contact?.validContacts?.[item["Assigned To Email"]]?.[3])
        .filter(Boolean) || [];
    const newlySelectedCompanies = selectedContractor
      .map((contractor) => contractor.companyName)
      .filter(Boolean);

    return [...new Set([...existingCompanies, ...newlySelectedCompanies])];
  }, [contact, selectedContractor]);

  // Persists the current manager/contractor selections to the legacy assignment API.
  const persistAssignments = useCallback(
    async ({
      shouldUpdateResponsibleContractor = false,
      responsibleContractorValue = "",
    } = {}) => {
      const managerEmails = selectedManager.map((manager) => manager.email);
      const contractorEmails = selectedContractor.map(
        (contractor) => contractor.email,
      );
      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");
      const syncToken = beginSyncActivity?.("Saving assignments");

      try {
        await SideBarEndService.assignTask({
          code,
          loginEmail,
          company_id,
          project_id: id,
          managerEmails,
          contractorEmails,
          selectedItems: [
            {
              id: selectedEntryId,
              submittalTitle: content?.specTitle,
              submittalNumber: content?.specNumber,
              submittalRevision: content?.specRevision,
            },
          ],
          action: sendNotification,
        });

        if (
          shouldUpdateResponsibleContractor &&
          onResponsibleContractorChange &&
          content?.entryOrigin !== "Procore"
        ) {
          onResponsibleContractorChange(responsibleContractorValue || "");
        }

        setSelectedManager([]);
        setSelectedContractor([]);
        setSelectedItems([]);
        setSelectedCompany(null);
        finishSyncActivity?.(syncToken);
      } catch (error) {
        console.error("Error sending invitations", error);
        failSyncActivity?.(
          syncToken,
          "Failed to save assignments. You may no longer be synced to the latest version.",
        );
      } finally {
        void fetchTasksAssignedInfo(selectedEntryId);
      }
    },
    [
      beginSyncActivity,
      company_id,
      content,
      failSyncActivity,
      fetchTasksAssignedInfo,
      finishSyncActivity,
      id,
      onResponsibleContractorChange,
      selectedContractor,
      selectedEntryId,
      selectedManager,
      sendNotification,
    ],
  );

  // Sends the assignment request after validating company/overwrite edge cases.
  const handleSendInvitation = useCallback(async () => {
    if (
      allContractorCompanies.length > 1 &&
      content?.entryOrigin !== "Procore" &&
      overwriteField
    ) {
      setIsCompanyModalVisible(true);
      return;
    }

    if (
      content?.responsibleContractor &&
      selectedContractor.length > 0 &&
      content?.entryOrigin !== "Procore"
    ) {
      setIsOverwriteModalVisible(true);
      return;
    }

    await persistAssignments({
      shouldUpdateResponsibleContractor:
        selectedContractor.length > 0 &&
        overwriteField &&
        content?.entryOrigin !== "Procore",
      responsibleContractorValue: selectedContractor[0]?.companyName || "",
    });
  }, [
    allContractorCompanies.length,
    content,
    overwriteField,
    persistAssignments,
    selectedContractor,
  ]);

  // Applies the chosen company after the user resolves the multi-company modal.
  const handleCompanySelection = useCallback(() => {
    setIsCompanyModalVisible(false);

    if (!selectedCompany) {
      return;
    }

    if (content?.responsibleContractor) {
      setIsOverwriteModalVisible(true);
      return;
    }

    void persistAssignments({
      shouldUpdateResponsibleContractor: true,
      responsibleContractorValue: selectedCompany,
    });
  }, [content?.responsibleContractor, persistAssignments, selectedCompany]);

  // Applies the user's overwrite decision before saving the assignments.
  const handleOverwriteSelection = useCallback(
    (shouldOverwrite) => {
      setIsOverwriteModalVisible(false);

      void persistAssignments({
        shouldUpdateResponsibleContractor:
          shouldOverwrite && content?.entryOrigin !== "Procore",
        responsibleContractorValue:
          selectedCompany || selectedContractor[0]?.companyName || "",
      });
    },
    [
      content?.entryOrigin,
      persistAssignments,
      selectedCompany,
      selectedContractor,
    ],
  );

  // Removes one or more existing task assignments from the active submittal.
  const handleDeleteAssignments = useCallback(
    async (tasksToDelete) => {
      if (!tasksToDelete?.length) {
        return;
      }

      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");
      const deletePayloadByTaskId = new Map();

      tasksToDelete.forEach((task) => {
        const taskId = firstFilled(task?.["Task id"]);
        const assignedEmail = firstFilled(task?.["Assigned To Email"]);

        if (!taskId || !assignedEmail) {
          return;
        }

        if (!deletePayloadByTaskId.has(taskId)) {
          deletePayloadByTaskId.set(taskId, {
            code,
            loginEmail,
            company_id,
            project_id: id,
            id: taskId,
            assignedEmails: [],
          });
        }

        const currentPayload = deletePayloadByTaskId.get(taskId);
        if (!currentPayload.assignedEmails.includes(assignedEmail)) {
          currentPayload.assignedEmails.push(assignedEmail);
        }
      });

      const deletePayload = [...deletePayloadByTaskId.values()];
      if (!deletePayload.length) {
        return;
      }

      const syncToken = beginSyncActivity?.(
        deletePayload.length === 1 &&
          deletePayload[0]?.assignedEmails?.length === 1
          ? "Removing assignment"
          : "Removing assignments",
      );

      try {
        await SideBarEndService.deleteTask(deletePayload);
        setSelectedAssignmentEmails((previousEmails) =>
          previousEmails.filter(
            (email) =>
              !deletePayload.some((payload) =>
                payload.assignedEmails.includes(email),
              ),
          ),
        );
        finishSyncActivity?.(syncToken);
      } catch (error) {
        console.error("Failed to delete assignment", error);
        failSyncActivity?.(
          syncToken,
          "Failed to remove the assignment(s). You may no longer be synced to the latest version.",
        );
      } finally {
        void fetchTasksAssignedInfo(selectedEntryId);
      }
    },
    [
      beginSyncActivity,
      company_id,
      failSyncActivity,
      fetchTasksAssignedInfo,
      finishSyncActivity,
      id,
      selectedEntryId,
    ],
  );

  const handleAssignmentSelectionChange = useCallback((email, checked) => {
    setSelectedAssignmentEmails((previousEmails) => {
      if (checked) {
        return previousEmails.includes(email)
          ? previousEmails
          : [...previousEmails, email];
      }

      return previousEmails.filter(
        (previousEmail) => previousEmail !== email,
      );
    });
  }, []);

  const handleDeleteAssignment = useCallback(
    async (task) => {
      await handleDeleteAssignments([task]);
    },
    [handleDeleteAssignments],
  );

  const handleDeleteSelectedAssignments = useCallback(async () => {
    await handleDeleteAssignments(selectedAssignedTasks);
  }, [handleDeleteAssignments, selectedAssignedTasks]);

  // Determines whether the actual-delivery workflow control should be locked.
  const isWorkflowLocked = Boolean(isLocked || workflowLockDetails?.isLocked);

  // Persists the actual delivery date back through the beta table update pipeline.
  const handleUpdateActualDeliveryDate = useCallback(() => {
    handleColumnChange?.([
      { name: "actualDeliveryDate", value: selectedActualDeliveryDate },
      { name: "isEdited", value: true },
    ]);
  }, [handleColumnChange, selectedActualDeliveryDate]);

  // Disables non-working days, holidays, and future dates in the actual-delivery picker.
  const isDateDisabled = useCallback(
    (date) => {
      if (!date) {
        return false;
      }

      const formattedDate =
        typeof date.format === "function"
          ? date.format("YYYY-MM-DD")
          : dayjs(date).format("YYYY-MM-DD");
      const dayName =
        typeof date.format === "function"
          ? date.format("dddd")
          : dayjs(date).format("dddd");

      return (
        calendarConfig.disabledWeekdays.has(dayName) ||
        calendarConfig.disabledDates.has(formattedDate) ||
        dayjs(date).isAfter(dayjs(), "day")
      );
    },
    [calendarConfig],
  );

  // Filters cascader search matches against any label in the selected hierarchy path.
  const filterCascaderOption = useCallback(
    (inputValue, path) =>
      path.some((option) =>
        option.searchValue
          ?.toLowerCase()
          .includes(String(inputValue).toLowerCase()),
      ),
    [],
  );

  if (!content) {
    return null;
  }

  if (isSidebarLoading) {
    return <ProcurementSideMenuContentLoadingState />;
  }

  return (
    <div className={styles.panel}>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Assignments</h3>
          <Button
            className={styles.secondaryButton}
            disabled={isBulkRemoveDisabled}
            onClick={handleDeleteSelectedAssignments}
          >
            Remove All Selected
          </Button>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>
            Assign Procurement Manager
          </label>
          <Select
            mode="multiple"
            placeholder="Select Manager(s)"
            value={selectedManagerValues.firstName}
            onChange={handleManagerSelectChange}
            size="large"
            maxTagCount={2}
            className={styles.pillSelect}
            options={filteredOptions.map(([email, [firstName, lastName]]) => ({
              label: `${firstName} ${lastName}`,
              value: `${email},${firstName} ${lastName}`,
            }))}
          />
        </div>

        <div className={styles.assigneeList}>
          {assignedManagers.length === 0 ? (
            <div className={styles.emptyState}>
              No procurement managers assigned
            </div>
          ) : (
            assignedManagers.map((item) => {
              const assignedToName = item["Assigned To Name"];
              const initials = assignedToName
                .split(" ")
                .slice(0, 2)
                .map((word) => word.charAt(0))
                .join("");

              return (
                <div
                  className={styles.assigneeRow}
                  key={item["Assigned To Email"]}
                >
                  <div className={styles.assigneeInfo}>
                    <Avatar className={styles.assigneeAvatar}>
                      {initials}
                    </Avatar>
                    <span className={styles.assigneeName}>
                      {assignedToName}
                    </span>
                  </div>
                  <div className={styles.assigneeControls}>
                    <Checkbox
                      checked={selectedAssignmentEmails.includes(
                        item["Assigned To Email"],
                      )}
                      disabled={accessLevel < 3}
                      onChange={(event) =>
                        handleAssignmentSelectionChange(
                          item["Assigned To Email"],
                          event.target.checked,
                        )
                      }
                    />
                    <DeleteOutlineOutlinedIcon
                      className={`${styles.deleteButton} ${accessLevel < 3 ? styles.deleteButtonDisabled : ""}`}
                      onClick={
                        accessLevel < 3
                          ? undefined
                          : () => handleDeleteAssignment(item)
                      }
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>
            Assign Responsible Contractor
          </label>
          <Select
            mode="multiple"
            placeholder="Select Contractor(s)"
            value={selectedContractorValues.firstName}
            onChange={handleContractorSelectChange}
            size="large"
            maxTagCount={2}
            className={styles.pillSelect}
            options={filteredOptions.map(
              ([email, [firstName, lastName, _emailAddress, companyName]]) => ({
                label: `${firstName} ${lastName} - ${companyName}`,
                value: `${email},${firstName} ${lastName}, ${companyName}`,
              }),
            )}
          />
        </div>

        <div className={styles.assigneeList}>
          {assignedContractors.length === 0 ? (
            <div className={styles.emptyState}>
              No responsible contractors assigned
            </div>
          ) : (
            assignedContractors.map((item) => {
              const assignedToName = item["Assigned To Name"];
              const initials = assignedToName
                .split(" ")
                .slice(0, 2)
                .map((word) => word.charAt(0))
                .join("");

              return (
                <div
                  className={styles.assigneeRow}
                  key={item["Assigned To Email"]}
                >
                  <div className={styles.assigneeInfo}>
                    <Avatar className={styles.assigneeAvatar}>
                      {initials}
                    </Avatar>
                    <span className={styles.assigneeName}>
                      {assignedToName}
                    </span>
                  </div>
                  <div className={styles.assigneeControls}>
                    <Checkbox
                      checked={selectedAssignmentEmails.includes(
                        item["Assigned To Email"],
                      )}
                      disabled={accessLevel < 3}
                      onChange={(event) =>
                        handleAssignmentSelectionChange(
                          item["Assigned To Email"],
                          event.target.checked,
                        )
                      }
                    />
                    <DeleteOutlineOutlinedIcon
                      className={`${styles.deleteButton} ${accessLevel < 3 ? styles.deleteButtonDisabled : ""}`}
                      onClick={
                        accessLevel < 3
                          ? undefined
                          : () => handleDeleteAssignment(item)
                      }
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.switchRow}>
          <Switch defaultChecked onChange={handleNotificationChange} />
          <span>Notify assigned users</span>
        </div>

        <div className={styles.actionRow}>
          <Button
            className={styles.primaryButton}
            disabled={!isAssignmentButtonVisible}
            onClick={handleSendInvitation}
            type="primary"
          >
            Save Assignments
          </Button>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Dependencies</h3>
        <div className={styles.twoColumnGrid}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Linked Items</label>
            <Cascader
              className={styles.pillCascader}
              options={dependencyOptions}
              displayRender={(labels) => labels[labels.length - 1]}
              value={cascaderValue}
              onChange={(values) => {
                setCascaderValue(values);
                handleSuccessorChange(values);
              }}
              popupMenuColumnStyle={{ maxWidth: 350 }}
              placeholder="Select a submittal"
              size="large"
              showSearch={{ filter: filterCascaderOption }}
              multiple
              maxTagCount={2}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Predecessors</label>
            <Cascader
              className={styles.pillCascader}
              options={dependencyOptions}
              displayRender={(labels) => labels[labels.length - 1]}
              value={predecessorCascaderValue}
              onChange={(values) => {
                setPredecessorCascaderValue(values);
                handlePredecessorChange(values);
              }}
              popupMenuColumnStyle={{ maxWidth: 350 }}
              placeholder="Select a submittal"
              size="large"
              showSearch={{ filter: filterCascaderOption }}
              multiple
              maxTagCount={2}
            />
          </div>
        </div>

        <div className={styles.actionRow}>
          <Button
            className={styles.primaryButton}
            disabled={!saveDependenciesFlag}
            onClick={handleSaveDependencies}
            type="primary"
          >
            Save Dependencies
          </Button>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Workflow</h3>
        <div className={styles.fieldGroup}>
          <label htmlFor="actualDeliveryDate" className={styles.fieldLabel}>
            Actual Delivery Date
          </label>
          <DatePicker
            id="actualDeliveryDate"
            format="MM-DD-YYYY"
            disabled={isWorkflowLocked}
            value={
              selectedActualDeliveryDate
                ? parsePickerDate(selectedActualDeliveryDate)
                : null
            }
            className={styles.pillDatePicker}
            onChange={(date) => {
              setSelectedActualDeliveryDate(
                date ? date.format("MM-DD-YYYY") : "",
              );
            }}
            placeholder="Select Date"
            disabledDate={isDateDisabled}
          />
        </div>

        <div className={styles.actionRow}>
          <Button
            className={styles.primaryButton}
            // shape="round"
            disabled={isWorkflowLocked}
            onClick={handleUpdateActualDeliveryDate}
            type="primary"
          >
            Complete Workflow
          </Button>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Item Activity Tracking</h3>
        <div className={styles.activityList}>
          {(contact?.tasksAssigned?.contentHistory || []).length === 0 ? (
            <div className={styles.emptyState}>No activity history yet</div>
          ) : (
            contact?.tasksAssigned?.contentHistory?.map((item) => {
              const assignmentDateTime = item["Assignment Date"] || "";
              const [date = "", time = ""] = assignmentDateTime.split(" ");

              return (
                <div className={styles.activityItem} key={item.id}>
                  <img
                    src={InfoIcon}
                    alt="Info"
                    className={styles.activityIcon}
                  />
                  <div className={styles.activityContent}>
                    <p className={styles.activityTitle}>
                      {item["Action"] === "Assigned" &&
                        `${item["Assigned By Name"]} ${item["Action"].toLowerCase()} ${item["Assigned To Name"]} as ${item["Role"]}`}

                      {item["Action"] === "Updated" &&
                        `${item["Assigned By Name"]} ${item["Action"].toLowerCase()} ${item["Updated By Name"]}'s task as completed`}

                      {item["Action"] === "Deleted" &&
                        `${item["Assigned By Name"]} ${item["Action"].toLowerCase()} ${item["Assigned To Name"]} as ${item["Role"]}`}

                      {item["Action"] !== "Assigned" &&
                        item["Action"] !== "Updated" &&
                        item["Action"] !== "Deleted" &&
                        `${item["Assigned By Name"]} ${item["Action"].toLowerCase()}`}
                    </p>
                    <span className={styles.activityMeta}>
                      Added {date} at {time}
                    </span>
                  </div>
                  <img
                    src={increaseArrow}
                    alt="Expand"
                    className={styles.activityArrow}
                  />
                </div>
              );
            })
          )}
        </div>
      </section>

      <Modal
        title="You've selected contractors from multiple companies"
        open={isCompanyModalVisible}
        onCancel={() => setIsCompanyModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            className={styles.secondaryButton}
            onClick={() => setIsCompanyModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="done"
            type="primary"
            className={styles.primaryButton}
            onClick={handleCompanySelection}
            disabled={!selectedCompany}
          >
            Done
          </Button>,
        ]}
      >
        <p className={styles.modalText}>
          Please choose one company to display as the responsible contractor in
          the procurement log.
        </p>
        <Select
          className={styles.pillSelect}
          placeholder="Select a responsible contractor company"
          value={selectedCompany}
          onChange={setSelectedCompany}
          options={allContractorCompanies.map((company) => ({
            value: company,
            label: company,
          }))}
        />
      </Modal>

      <Modal
        title="Update Responsible Contractor"
        open={isOverwriteModalVisible}
        onCancel={() => setIsOverwriteModalVisible(false)}
        footer={[
          <Button
            key="no"
            className={styles.secondaryButton}
            onClick={() => handleOverwriteSelection(false)}
          >
            No
          </Button>,
          <Button
            key="yes"
            type="primary"
            className={styles.primaryButton}
            onClick={() => handleOverwriteSelection(true)}
          >
            Yes
          </Button>,
        ]}
      >
        <p className={styles.modalText}>
          The company <strong>{content?.responsibleContractor || ""}</strong> is
          marked as the responsible contractor in the procurement log. Do you
          want to overwrite it?
        </p>
      </Modal>
    </div>
  );
}

export default ProcurementSideMenuContent;
